import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { AlertCircle, CheckCircle, Truck, MapPin, Loader2, Package, Copy, Check, ExternalLink, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const { user, token, isInternalBuyer } = useAuth();
  const { cart, fetchCart } = useCart();
  const { currency, exchangeRate, formatPrice, fetchExchangeRate } = useCurrency();
  const navigate = useNavigate();
  const [discountRules, setDiscountRules] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState('');
  const [shippingAddress, setShippingAddress] = useState(user?.shipping_address || '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [copiedTracking, setCopiedTracking] = useState(false);

  // ---- Envia.com shipping integration ----
  const [destZip, setDestZip] = useState('');
  const [destCity, setDestCity] = useState('');
  const [destState, setDestState] = useState('');
  const [enviaQuotes, setEnviaQuotes] = useState([]);
  const [enviaLoading, setEnviaLoading] = useState(false);
  const [enviaError, setEnviaError] = useState(null);
  const [selectedEnviaQuote, setSelectedEnviaQuote] = useState(null);

  // ---- Postal code auto-fill ----
  // Cuando el usuario captura 5 dígitos, consultamos zippopotam.us para
  // auto-rellenar ciudad y estado. Los campos quedan en modo read-only
  // hasta que el usuario haga click en "editar".
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupError, setZipLookupError] = useState(null);
  const [zipAutofilled, setZipAutofilled] = useState(false);
  const [fieldsLocked, setFieldsLocked] = useState(false);

  // ---- Shipping insurance ----
  // declaredValueInput holds the user's typed value IN THE CURRENT CURRENCY (USD or MXN).
  // We auto-convert it when the currency toggle changes so the canonical MXN amount stays stable.
  const [insureShipment, setInsureShipment] = useState(false);
  const [declaredValueInput, setDeclaredValueInput] = useState('');
  const prevCurrencyRef = useRef(currency);

  useEffect(() => {
    if (prevCurrencyRef.current !== currency && declaredValueInput) {
      const n = Number(declaredValueInput);
      if (Number.isFinite(n) && n > 0 && exchangeRate?.value) {
        let converted = n;
        if (currency === 'USD' && prevCurrencyRef.current === 'MXN') {
          converted = n / exchangeRate.value;
        } else if (currency === 'MXN' && prevCurrencyRef.current === 'USD') {
          converted = n * exchangeRate.value;
        }
        setDeclaredValueInput(converted.toFixed(2));
      }
    }
    prevCurrencyRef.current = currency;
  }, [currency, exchangeRate?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive canonical MXN amount + insurance cost in MXN (2% of declared value)
  const declaredValueRaw = Number(declaredValueInput);
  const declaredValueMXN = (() => {
    if (!Number.isFinite(declaredValueRaw) || declaredValueRaw <= 0) return 0;
    if (currency === 'USD' && exchangeRate?.value) return declaredValueRaw * exchangeRate.value;
    return declaredValueRaw;
  })();
  const insuranceCostMXN = insureShipment && declaredValueMXN > 0
    ? Math.round(declaredValueMXN * 0.02 * 100) / 100
    : 0;

  // Display helper: shipping prices come in MXN from Envia; convert to USD if toggle is USD.
  const formatShippingPrice = (priceMXN) => {
    if (!priceMXN) return 'Gratis';
    if (currency === 'USD' && exchangeRate?.value) {
      const inUSD = priceMXN / exchangeRate.value;
      return `$${inUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
    }
    return `$${priceMXN.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`;
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchCart();
    // El checkout SIEMPRE muestra MXN, sin importar el toggle del navbar.
    // Forzamos la carga del tipo de cambio Banxico aunque el toggle esté en USD.
    if (!exchangeRate?.value && fetchExchangeRate) {
      fetchExchangeRate();
    }
    axios.get(`${API}/discount-rules`).then(r => setDiscountRules(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user, navigate, fetchCart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill city/state from Mexican postal code (zippopotam.us)
  useEffect(() => {
    // Reset cuando el usuario está editando un CP incompleto
    if (destZip.length !== 5) {
      setZipLookupError(null);
      setZipLookupLoading(false);
      setZipAutofilled(false);
      setFieldsLocked(false);
      return;
    }

    let cancelled = false;
    const lookup = async () => {
      setZipLookupLoading(true);
      setZipLookupError(null);
      try {
        const resp = await fetch(`https://api.zippopotam.us/mx/${destZip}`);
        if (cancelled) return;
        if (!resp.ok) {
          throw new Error(resp.status === 404 ? 'not_found' : `http_${resp.status}`);
        }
        const data = await resp.json();
        const place = (data?.places || [])[0];
        if (!place) throw new Error('no_places');

        const city = place['place name'] || '';
        const state = place['state abbreviation'] || place['state'] || '';
        setDestCity(city);
        setDestState(state);
        setZipAutofilled(true);
        setFieldsLocked(true);
      } catch (e) {
        if (cancelled) return;
        setZipLookupError('Código postal no encontrado. Verifica e intenta de nuevo.');
        setZipAutofilled(false);
        setFieldsLocked(false);
        setDestCity('');
        setDestState('');
      } finally {
        if (!cancelled) setZipLookupLoading(false);
      }
    };
    lookup();
    return () => { cancelled = true; };
  }, [destZip]);

  // Auto-fetch Envia.com quotes when destination is complete (debounced)
  useEffect(() => {
    if (destZip.length !== 5 || !destCity.trim() || !destState.trim()) {
      setEnviaQuotes([]);
      setEnviaError(null);
      return;
    }
    const timer = setTimeout(async () => {
      setEnviaLoading(true);
      setEnviaError(null);
      setEnviaQuotes([]);
      setSelectedEnviaQuote(null);
      setSelectedShipping('');
      try {
        const res = await axios.post(`${API}/envia/quote`, {
          destination_zip: destZip,
          destination_city: destCity.trim(),
          destination_state: destState.trim(),
          weight: 1,
          length: 20,
          width: 20,
          height: 20,
        });
        const quotes = res.data?.quotes || [];
        setEnviaQuotes(quotes);
        if (quotes.length === 0) {
          setEnviaError(res.data?.message || 'No hay cotizaciones disponibles para esta ruta');
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        setEnviaError(typeof detail === 'string' ? detail : 'No se pudieron obtener cotizaciones de Envia.com');
      } finally {
        setEnviaLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [destZip, destCity, destState]);

  const handleSelectShipping = (value) => {
    setSelectedShipping(value);
    if (value && value.startsWith('envia-')) {
      const idx = parseInt(value.split('-')[1], 10);
      setSelectedEnviaQuote(enviaQuotes[idx] || null);
    } else {
      setSelectedEnviaQuote(null);
    }
  };

  const items = cart.items || [];
  const subtotal = cart.total || 0;
  const totalItems = items.reduce((a, i) => a + i.quantity, 0);
  const applicableRule = discountRules.sort((a, b) => b.min_qty - a.min_qty).find(r => totalItems >= r.min_qty);
  const discountAmount = applicableRule ? Math.round(subtotal * (applicableRule.discount_percent / 100) * 100) / 100 : 0;
  // Subtotal and product prices are stored in USD (base currency).
  // Shipping comes in MXN from Envia.com; convert to USD for the total when toggle is USD.
  const shippingCostMXN = selectedEnviaQuote?.total_price ?? 0;
  const shippingCostInUSD = exchangeRate?.value ? shippingCostMXN / exchangeRate.value : shippingCostMXN;
  // Insurance is canonically in MXN; convert to USD when needed for USD totals.
  const insuranceCostInUSD = exchangeRate?.value ? insuranceCostMXN / exchangeRate.value : insuranceCostMXN;
  const total = Math.round((subtotal - discountAmount + shippingCostInUSD + insuranceCostInUSD) * 100) / 100;

  const handleCheckout = async () => {
    // Internal buyers skip carrier selection AND insurance — pickup-only flow.
    if (!isInternalBuyer) {
      if (!selectedShipping) { toast.error('Seleccione un método de envío'); return; }
      if (insureShipment && declaredValueMXN <= 0) {
        toast.error('Ingresa un valor declarado mayor a 0 para asegurar el envío');
        return;
      }
    }
    setLoading(true);
    try {
      // ---- Persist the chosen Envia.com carrier/service/price ONLY ----
      // The actual shipping label is generated AFTER the payment is confirmed
      // via the Stripe webhook (see backend/routes/payments.py
      // → _generate_shipment_after_payment). This prevents Envia.com from
      // being charged for orders that are never paid (abandoned checkout).
      // We also send the destination fields so the webhook has everything
      // it needs to build the Envia.com request.
      // Skipped entirely for internal buyers (in-warehouse pickup, no shipping).
      const enviaPayload = {};
      if (!isInternalBuyer && selectedEnviaQuote) {
        enviaPayload.envia_carrier = selectedEnviaQuote.carrier;
        enviaPayload.envia_service = selectedEnviaQuote.service;
        enviaPayload.envia_price_mxn = selectedEnviaQuote.total_price;
        // Destination snapshot — used by the post-payment webhook
        enviaPayload.destination_zip = destZip;
        enviaPayload.destination_city = destCity;
        enviaPayload.destination_state = destState;
      }

      const orderBody = {
        shipping_provider_id: null,
        shipping_address: shippingAddress,
        notes,
        ...enviaPayload,
        // ---- Insurance fields (always sent so backend can store them) ----
        // Internal buyers always skip insurance — backend also enforces this.
        insured: !isInternalBuyer && insureShipment && declaredValueMXN > 0,
        declared_value: !isInternalBuyer && insureShipment ? Math.round(declaredValueMXN * 100) / 100 : 0,
        insurance_cost_mxn: !isInternalBuyer && insureShipment ? insuranceCostMXN : 0,
      };

      const res = await axios.post(`${API}/orders/checkout`, orderBody, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const createdOrder = res.data;

      // ---- Stripe redirection for non-internal customers ----
      // Internal buyer purchases skip payment entirely (order_type === 'interna').
      // For all other customers, we create a Stripe Checkout session and redirect
      // them to the hosted checkout page. After paying (or cancelling), Stripe
      // redirects back to /pedido/:id with ?payment=success or ?payment=cancelled.
      if (createdOrder?.order_type !== 'interna') {
        try {
          const sessionRes = await axios.post(
            `${API}/payments/create-session`,
            { order_id: createdOrder.id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const url = sessionRes.data?.session_url;
          if (url) {
            toast.success('Redirigiendo a la pasarela de pago…');
            // Persist the order id so we can read it back if the user closes the tab.
            try { sessionStorage.setItem('kt_pending_order_id', createdOrder.id); } catch {}
            // Hard navigation — Stripe is hosted on a different origin.
            window.location.href = url;
            return;
          }
          toast.error('No se pudo obtener el enlace de pago. Puedes pagar desde el detalle del pedido.');
        } catch (payErr) {
          const detail = payErr.response?.data?.detail;
          toast.error(
            typeof detail === 'string'
              ? `No se pudo iniciar el pago: ${detail}`
              : 'No se pudo iniciar el pago. Puedes intentarlo desde el detalle del pedido.'
          );
          // Fall through: show the local confirmation so they can retry from /pedido/:id
        }
      }

      setOrderResult(createdOrder);
      toast.success('Pedido confirmado correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar el pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTracking = async () => {
    const tn = orderResult?.envia_tracking_number;
    if (!tn) return;
    try {
      await navigator.clipboard.writeText(tn);
      setCopiedTracking(true);
      toast.success('Número de rastreo copiado');
      setTimeout(() => setCopiedTracking(false), 2000);
    } catch {
      toast.error('No se pudo copiar al portapapeles');
    }
  };

  if (orderResult) {
    return (
      <div className="py-16 md:py-20">
        <div className="mx-auto max-w-lg px-4 text-center space-y-6">
          <CheckCircle className="h-16 w-16 text-green-400 mx-auto" />
          <h1 className="text-2xl font-display font-semibold">Pedido Confirmado</h1>
          <p className="text-muted-foreground">Tu número de orden es:</p>
          <p className="text-3xl font-display font-bold" data-testid="confirmation-order-number">{orderResult.order_number}</p>

          {orderResult.insured && orderResult.declared_value > 0 && (
            <div
              className="mx-auto inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-200 text-sm"
              data-testid="confirmation-insurance-badge"
            >
              <Shield className="h-4 w-4 text-blue-300" />
              <span>
                Envío asegurado por&nbsp;
                <span className="font-semibold tabular-nums">
                  ${Number(orderResult.declared_value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN
                </span>
              </span>
            </div>
          )}

          {orderResult.envia_tracking_number && (
            <Card className="text-left">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                    Guía de envío {orderResult.envia_carrier?.toUpperCase()}
                  </span>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Número de Rastreo</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 rounded-md bg-secondary text-sm font-mono break-all" data-testid="tracking-number">
                      {orderResult.envia_tracking_number}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCopyTracking}
                      className="shrink-0 gap-1.5"
                      data-testid="copy-tracking-button"
                    >
                      {copiedTracking ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copiedTracking ? 'Copiado' : 'Copiar'}
                    </Button>
                  </div>
                </div>
                {orderResult.envia_label_url && (
                  <a
                    href={orderResult.envia_label_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    data-testid="label-download-link"
                  >
                    <Button type="button" variant="secondary" className="w-full gap-2">
                      <ExternalLink className="h-3.5 w-3.5" />
                      Descargar etiqueta PDF
                    </Button>
                  </a>
                )}
              </CardContent>
            </Card>
          )}

          {isInternalBuyer && <Badge className="bg-amber-100 text-amber-300 border-amber-200">Compra Interna - Sin Cobro</Badge>}
          {!isInternalBuyer && (
            <div
              className="bg-amber-950/40 border border-amber-800/60 rounded-lg p-4 text-sm text-amber-200 text-left"
              data-testid="checkout-pending-payment-notice"
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-300" />
                <div className="space-y-1">
                  <p className="font-medium">Tu pedido está pendiente de pago.</p>
                  <p className="text-amber-200/80">
                    No pudimos abrir la pasarela de pago automáticamente. Entra al detalle del pedido y
                    presiona <strong>“Pagar ahora”</strong> para completar tu compra.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-center gap-3 flex-wrap">
            {!isInternalBuyer && (
              <Button
                onClick={() => navigate(`/pedido/${orderResult.id}`)}
                data-testid="confirmation-pay-now-button"
              >
                Pagar ahora
              </Button>
            )}
            <Button variant={isInternalBuyer ? 'default' : 'outline'} onClick={() => navigate('/perfil')}>Ver Mis Pedidos</Button>
            <Button variant="outline" onClick={() => navigate('/tienda')}>Seguir Comprando</Button>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    navigate('/carrito');
    return null;
  }

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-display font-semibold tracking-tight mb-8">Checkout</h1>
        {isInternalBuyer && (
          <div className="bg-amber-950/40 border border-amber-800 rounded-lg p-4 mb-6 text-sm text-amber-300">
            <strong>Compra Interna:</strong> Este pedido se procesará sin cobro. El inventario se descontará automáticamente.
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* ----------------------------------------------------------------
                Shipping Method + Insurance.
                BOTH sections are hidden for internal_buyer users — internal
                purchases use in-warehouse pickup, with no carrier quote and
                no insurance applicable.
            ---------------------------------------------------------------- */}
            {!isInternalBuyer && (
              <>
                {/* Shipping Method (powered by Envia.com) */}
                <Card data-testid="shipping-method-card">
                  <CardContent className="p-6 space-y-5">
                    <h3 className="font-display font-semibold flex items-center gap-2"><Truck className="h-5 w-5" /> Método de Envío</h3>

                {/* Destination inputs that drive Envia.com quoting */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="dest-zip" className="text-xs">Código Postal *</Label>
                    <div className="relative">
                      <Input
                        id="dest-zip"
                        inputMode="numeric"
                        maxLength={5}
                        placeholder="64000"
                        value={destZip}
                        onChange={e => setDestZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        data-testid="checkout-zip-input"
                        aria-invalid={!!zipLookupError}
                        className={`pr-9 ${
                          zipAutofilled
                            ? 'border-emerald-500/50 focus-visible:ring-emerald-500/40'
                            : zipLookupError
                              ? 'border-rose-500/50 focus-visible:ring-rose-500/40'
                              : ''
                        }`}
                      />
                      {/* Status indicator inside CP input */}
                      <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center" aria-hidden="true">
                        {zipLookupLoading && (
                          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" data-testid="zip-lookup-spinner" />
                        )}
                        {!zipLookupLoading && zipAutofilled && (
                          <Check className="h-4 w-4 text-emerald-500" data-testid="zip-lookup-success" />
                        )}
                        {!zipLookupLoading && zipLookupError && (
                          <AlertCircle className="h-4 w-4 text-rose-400" data-testid="zip-lookup-error-icon" />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="dest-city" className="text-xs">Ciudad *</Label>
                    <div className="relative">
                      <Input
                        id="dest-city"
                        placeholder="Monterrey"
                        value={destCity}
                        onChange={e => setDestCity(e.target.value)}
                        data-testid="checkout-city-input"
                        readOnly={fieldsLocked}
                        className={`${zipAutofilled ? 'border-emerald-500/50 focus-visible:ring-emerald-500/40 pr-9' : ''} ${fieldsLocked ? 'bg-secondary/40 cursor-default' : ''}`}
                      />
                      {zipAutofilled && (
                        <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center" aria-hidden="true">
                          <Check className="h-4 w-4 text-emerald-500" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="dest-state" className="text-xs">Estado *</Label>
                    <div className="relative">
                      <Input
                        id="dest-state"
                        placeholder="Se llena automáticamente"
                        value={destState}
                        onChange={e => setDestState(e.target.value)}
                        maxLength={5}
                        data-testid="checkout-state-input"
                        readOnly={fieldsLocked}
                        className={`${zipAutofilled ? 'border-emerald-500/50 focus-visible:ring-emerald-500/40 pr-9' : ''} ${fieldsLocked ? 'bg-secondary/40 cursor-default' : ''}`}
                      />
                      {zipAutofilled && (
                        <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center" aria-hidden="true">
                          <Check className="h-4 w-4 text-emerald-500" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Inline status messages */}
                {zipLookupError ? (
                  <p
                    className="text-xs text-rose-400 flex items-center gap-1.5"
                    data-testid="zip-lookup-error-text"
                  >
                    <AlertCircle className="h-3 w-3" /> {zipLookupError}
                  </p>
                ) : fieldsLocked ? (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-emerald-400/90 flex items-center gap-1.5" data-testid="zip-autofill-success">
                      <Check className="h-3 w-3" /> Ciudad y estado completados automáticamente
                    </p>
                    <button
                      type="button"
                      onClick={() => setFieldsLocked(false)}
                      data-testid="zip-edit-fields-btn"
                      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors duration-150"
                    >
                      editar
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="h-3 w-3" /> Captura tu código postal y completaremos la ciudad y el estado por ti.
                  </p>
                )}

                {/* Loading skeleton */}
                {enviaLoading && (
                  <div className="space-y-2" data-testid="envia-loading">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="border rounded-lg p-3 flex items-center gap-3 animate-pulse">
                        <div className="h-4 w-4 rounded-full bg-muted" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 w-40 rounded bg-muted" />
                          <div className="h-2 w-24 rounded bg-muted" />
                        </div>
                        <div className="h-4 w-16 rounded bg-muted" />
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Consultando paqueterías en Envia.com…
                    </p>
                  </div>
                )}

                {/* Envia quotes */}
                {!enviaLoading && enviaQuotes.length > 0 && (
                  <RadioGroup value={selectedShipping} onValueChange={handleSelectShipping}>
                    <div className="space-y-2" data-testid="envia-quotes-list">
                      {enviaQuotes.map((q, idx) => {
                        const value = `envia-${idx}`;
                        return (
                          <div key={value} className="flex items-center space-x-3 border rounded-lg p-3 hover:bg-secondary/30 transition-colors duration-150">
                            <RadioGroupItem value={value} id={value} data-testid={`envia-option-${idx}`} />
                            <Label htmlFor={value} className="flex-1 cursor-pointer">
                              <div className="flex justify-between items-center gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="p-1.5 rounded-md bg-secondary shrink-0"><Package className="h-3.5 w-3.5" /></div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-sm truncate">{q.service_description || q.carrier}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                      {q.carrier?.toUpperCase()}{q.estimated_delivery ? ` • ${q.estimated_delivery}` : ''}
                                    </p>
                                  </div>
                                </div>
                                <span className="font-display font-semibold text-sm shrink-0">
                                  {formatShippingPrice(q.total_price)}
                                </span>
                              </div>
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </RadioGroup>
                )}

                {/* No Envia results — clear empty/error state */}
                {!enviaLoading && enviaQuotes.length === 0 && (
                  <div
                    className="border border-dashed border-border rounded-lg px-4 py-6 text-center space-y-2"
                    data-testid="envia-empty-state"
                  >
                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary mb-1">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                    </div>
                    {destZip.length === 5 && destCity && destState ? (
                      <>
                        <p className="text-sm font-medium" data-testid="envia-empty-title">
                          No se pudieron cargar las opciones de envío
                        </p>
                        <p className="text-xs text-muted-foreground max-w-md mx-auto">
                          {enviaError || 'Por favor verifica tu código postal e intenta de nuevo.'}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground max-w-md mx-auto">
                        Captura tu código postal, ciudad y estado para ver las opciones de envío disponibles.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Shipping Insurance toggle */}
            <Card
              data-testid="insurance-card"
              data-insurance-active={insureShipment}
              className={`transition-colors duration-200 ${insureShipment ? 'border-blue-500/40 bg-blue-500/[0.04]' : ''}`}
            >
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-md shrink-0 ${insureShipment ? 'bg-blue-500/15' : 'bg-secondary'}`}>
                      <Shield className={`h-4 w-4 ${insureShipment ? 'text-blue-300' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold flex items-center gap-2">
                        Asegurar mi envío
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Opcional</span>
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                        Protege tu paquete por hasta el valor declarado en caso de pérdida o daño durante el tránsito.
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={insureShipment}
                    onCheckedChange={(v) => {
                      setInsureShipment(v);
                      if (!v) setDeclaredValueInput('');
                    }}
                    data-testid="insurance-toggle"
                    aria-label="Activar seguro de envío"
                  />
                </div>

                {insureShipment && (
                  <div className="pl-0 sm:pl-11 space-y-3" data-testid="insurance-details">
                    <div className="space-y-1.5">
                      <Label htmlFor="declared-value" className="text-xs">
                        Valor declarado del paquete ({currency})
                      </Label>
                      <Input
                        id="declared-value"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={currency === 'USD' ? '100.00' : '2000.00'}
                        value={declaredValueInput}
                        onChange={(e) => setDeclaredValueInput(e.target.value)}
                        data-testid="declared-value-input"
                        className="max-w-xs"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md bg-blue-500/[0.06] border border-blue-500/20 px-3 py-2.5">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Shield className="h-3.5 w-3.5 text-blue-300" />
                        <span>Tu paquete estará asegurado hasta el valor declarado.</span>
                      </div>
                      <div className="text-sm font-display font-semibold text-blue-200 tabular-nums" data-testid="insurance-cost-display">
                        Costo del seguro:&nbsp;{formatShippingPrice(insuranceCostMXN)}
                      </div>
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                      Tarifa: 2% del valor declarado (cobrado al confirmar). El monto se reporta en MXN a la paquetería.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
              </>
            )}

            {/* Internal-buyer pickup notice — replaces the carrier/insurance UI */}
            {isInternalBuyer && (
              <Card
                className="border-emerald-500/30 bg-emerald-500/[0.04]"
                data-testid="internal-pickup-card"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-emerald-500/15 shrink-0">
                      <Package className="h-4 w-4 text-emerald-300" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-display font-semibold text-emerald-100">
                        Retiro en almacén
                      </h3>
                      <p className="text-sm text-emerald-200/80 leading-relaxed">
                        Las compras internas se entregan directamente desde el almacén — no requieren
                        paquetería ni seguro de envío. Indica abajo la ubicación interna a la que
                        debe enviarse el material.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display font-semibold">Dirección de Envío</h3>
                <Input value={shippingAddress} onChange={e => setShippingAddress(e.target.value)} placeholder="Dirección completa de envío" />
                <div className="space-y-2">
                  <Label>Notas (opcional)</Label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Instrucciones especiales..." rows={2} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-20">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display font-semibold">Resumen del Pedido</h3>
                <div className="space-y-2 text-sm">
                  {items.map(item => (
                    <div key={item.product_id} className="flex justify-between">
                      <span className="text-muted-foreground truncate mr-2">{item.product?.name} x{item.quantity}</span>
                      <span>{formatPrice(item.item_total)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-3 space-y-2 text-sm" data-testid="checkout-summary-mxn">
                  {/* Helper local: cualquier MXN se imprime en es-MX con sufijo MXN */}
                  {(() => {
                    const rate = exchangeRate?.value || 0;
                    const subtotalMXN = rate ? subtotal * rate : 0;
                    const discountMXN = rate ? discountAmount * rate : 0;
                    const totalMXN = subtotalMXN - discountMXN + shippingCostMXN + insuranceCostMXN;
                    const fmtMXN = (n) =>
                      `$${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`;
                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span data-testid="checkout-subtotal-mxn">
                            {rate ? fmtMXN(subtotalMXN) : formatPrice(subtotal)}
                          </span>
                        </div>
                        {applicableRule && (
                          <div className="flex justify-between text-green-400">
                            <span>Descuento ({applicableRule.discount_percent}%)</span>
                            <span data-testid="checkout-discount-mxn">
                              -{rate ? fmtMXN(discountMXN) : formatPrice(discountAmount)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Envío</span>
                          <span data-testid="checkout-shipping-display">
                            {isInternalBuyer
                              ? 'Retiro en almacén'
                              : (shippingCostMXN > 0 ? fmtMXN(shippingCostMXN) : 'Gratis')}
                          </span>
                        </div>
                        {insureShipment && insuranceCostMXN > 0 && (
                          <div className="flex justify-between text-blue-300" data-testid="checkout-insurance-line">
                            <span className="flex items-center gap-1.5">
                              <Shield className="h-3 w-3" />
                              Seguro
                            </span>
                            <span data-testid="checkout-insurance-display">
                              {fmtMXN(insuranceCostMXN)}
                            </span>
                          </div>
                        )}
                        <div className="border-t pt-3 flex justify-between font-display font-semibold text-lg">
                          <span>Total</span>
                          <span data-testid="checkout-total-display">
                            {rate ? fmtMXN(totalMXN) : formatPrice(total)}
                          </span>
                        </div>
                        {rate ? (
                          <p
                            className="text-[11px] text-muted-foreground italic leading-snug"
                            data-testid="checkout-mxn-note"
                          >
                            * Los precios se muestran en MXN al tipo de cambio del día
                            (${rate.toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} MXN/USD).
                          </p>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
                {discountRules.length > 0 && !applicableRule && (
                  <p className="text-xs text-muted-foreground">Compra {discountRules[0]?.min_qty}+ artículos para obtener {discountRules[0]?.discount_percent}% de descuento</p>
                )}
                <Button onClick={handleCheckout} disabled={loading} className="w-full" data-testid="checkout-confirm-button">
                  {loading ? 'Procesando...' : isInternalBuyer ? 'Confirmar Compra Interna' : 'Confirmar Pedido'}
                </Button>
                {!isInternalBuyer && (
                  <p className="text-xs text-muted-foreground text-center">
                    Pago 100% seguro. Aceptamos tarjeta de crédito/débito y OXXO vía Stripe.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
